import React, { useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Upload, FileText, Download, CheckCircle2, AlertTriangle, Loader2, X } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

// Minimal CSV parser — handles quoted fields, commas inside quotes, CRLF.
function parseCsv(text) {
  const rows = [];
  let cur = [], field = "", inQuotes = false, i = 0;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i += 2; continue; }
      if (c === '"') { inQuotes = false; i++; continue; }
      field += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ",") { cur.push(field); field = ""; i++; continue; }
    if (c === "\r") { i++; continue; }
    if (c === "\n") { cur.push(field); rows.push(cur); cur = []; field = ""; i++; continue; }
    field += c; i++;
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
  // Drop trailing empty rows
  return rows.filter(r => r.some(v => (v ?? "").toString().trim() !== ""));
}

function coerce(value, type) {
  if (value == null) return value;
  const v = String(value).trim();
  if (v === "") return type === "bool" ? true : type === "num" || type === "int" ? 0 : "";
  if (type === "bool") {
    const s = v.toLowerCase();
    return !(s === "false" || s === "0" || s === "no" || s === "n" || s === "non-ac" || s === "nonac");
  }
  if (type === "int") return parseInt(v, 10) || 0;
  if (type === "num") return parseFloat(v) || 0;
  if (type === "list") return v.split("|").map(s => s.trim()).filter(Boolean);
  return v;
}

/**
 * CsvImportDialog
 * props:
 *   open, onOpenChange
 *   title (e.g., "Import Vehicles")
 *   endpoint (e.g., "/vehicles/bulk-import")
 *   schema: [{ key, header, type, required, default? }] — describes CSV columns
 *   sampleRow: object with example values (used to build template)
 *   onImported() — parent reload
 */
export default function CsvImportDialog({ open, onOpenChange, title, endpoint, schema, sampleRow, onImported }) {
  const [rows, setRows] = useState([]);       // preview rows (objects)
  const [errors, setErrors] = useState([]);   // per-row validation errors
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {created, updated, errors}
  const fileRef = useRef(null);

  const headers = useMemo(() => schema.map(s => s.header), [schema]);

  const reset = () => { setRows([]); setErrors([]); setResult(null); };

  const downloadTemplate = () => {
    const headerLine = headers.join(",");
    const sampleValues = schema.map(s => {
      const v = sampleRow[s.key];
      if (Array.isArray(v)) return v.join("|");
      const str = String(v ?? "");
      return str.includes(",") || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str;
    }).join(",");
    const blob = new Blob([`${headerLine}\n${sampleValues}\n`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${title.toLowerCase().replace(/\s+/g, "-")}-template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("CSV too large (max 5 MB)"); return; }
    const text = await file.text();
    ingest(text);
  };

  const ingest = (text) => {
    reset();
    const parsed = parseCsv(text);
    if (parsed.length < 2) { toast.error("CSV must have a header row and at least one data row"); return; }
    const [head, ...body] = parsed;
    const headerIdx = {};
    head.forEach((h, i) => { headerIdx[h.trim().toLowerCase()] = i; });
    const items = [];
    const errs = [];
    body.forEach((r, rowIdx) => {
      const obj = {};
      let rowErrors = [];
      schema.forEach(col => {
        const idx = headerIdx[col.header.toLowerCase()];
        const raw = idx == null ? undefined : r[idx];
        if ((raw == null || String(raw).trim() === "") && col.required) {
          rowErrors.push(`missing ${col.header}`);
        }
        const val = (raw == null || String(raw).trim() === "") && col.default !== undefined
          ? col.default
          : coerce(raw, col.type);
        obj[col.key] = val;
      });
      items.push(obj);
      if (rowErrors.length) errs.push({ row: rowIdx + 2, error: rowErrors.join(", ") });
    });
    setRows(items);
    setErrors(errs);
  };

  const doImport = async () => {
    if (errors.length) { toast.error("Fix validation errors before importing"); return; }
    if (rows.length === 0) { toast.error("No rows to import"); return; }
    setBusy(true);
    try {
      const r = await api.post(endpoint, { items: rows });
      setResult(r.data);
      toast.success(`Imported: ${r.data.created} new, ${r.data.updated} updated`);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    }
    setBusy(false);
  };

  const close = () => { reset(); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-[#ea580c]" /> {title}</DialogTitle>
        </DialogHeader>

        {!result && (
          <>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="text-sm font-semibold text-slate-800">1) Download the template</div>
              <p className="text-xs text-slate-600 mt-1">Get the exact columns we expect. Fill it in Excel/Google Sheets and save as CSV.</p>
              <Button size="sm" variant="outline" onClick={downloadTemplate} className="mt-2" data-testid="csv-download-template">
                <Download className="w-3.5 h-3.5 mr-1" /> Download CSV Template
              </Button>
            </div>

            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 mt-3">
              <div className="text-sm font-semibold text-slate-800">2) Upload your filled CSV</div>
              <p className="text-xs text-slate-600 mt-1">Existing rows (by vehicle number / route name) will be updated. New rows will be added.</p>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={e => handleFile(e.target.files?.[0])} data-testid="csv-file-input" />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="mt-2" data-testid="csv-pick-file">
                <FileText className="w-3.5 h-3.5 mr-1" /> Choose CSV file…
              </Button>
            </div>

            {rows.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-slate-800">3) Preview ({rows.length} rows)</div>
                  {errors.length > 0 && <div className="text-xs text-rose-600 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {errors.length} error(s)</div>}
                </div>
                <div className="rounded-xl border border-slate-200 overflow-auto max-h-64">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-slate-600 sticky top-0">
                      <tr>{headers.map(h => <th key={h} className="text-left px-3 py-2 font-semibold">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, 30).map((r, i) => (
                        <tr key={i} className="odd:bg-white even:bg-slate-50 border-t border-slate-100">
                          {schema.map(c => (
                            <td key={c.key} className="px-3 py-1.5 whitespace-nowrap max-w-[180px] truncate">
                              {Array.isArray(r[c.key]) ? r[c.key].join(" | ") : String(r[c.key] ?? "")}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {rows.length > 30 && <div className="p-2 text-[11px] text-slate-500 border-t">+ {rows.length - 30} more rows…</div>}
                </div>
                {errors.length > 0 && (
                  <div className="mt-2 rounded-lg bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800 max-h-32 overflow-auto">
                    {errors.map((e, i) => <div key={i}>Row {e.row}: {e.error}</div>)}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {result && (
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-center" data-testid="csv-result">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <div className="mt-2 font-display font-bold text-emerald-900 text-lg">Import complete</div>
            <div className="mt-1 text-sm text-emerald-800">
              <b>{result.created}</b> new · <b>{result.updated}</b> updated
              {result.errors?.length > 0 && <> · <b className="text-rose-700">{result.errors.length}</b> failed</>}
            </div>
            {result.errors?.length > 0 && (
              <div className="mt-3 text-left rounded-lg bg-white border border-rose-200 p-2 text-xs text-rose-800 max-h-40 overflow-auto">
                {result.errors.map((e, i) => <div key={i}>Row {e.row} ({e.vehicle_number || e.name}): {e.error}</div>)}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {rows.length > 0 && !result && (
            <Button onClick={doImport} disabled={busy || errors.length > 0} className="bg-[#ea580c] hover:bg-orange-700" data-testid="csv-import-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              Import {rows.length} row{rows.length === 1 ? "" : "s"}
            </Button>
          )}
          <Button variant="outline" onClick={close} data-testid="csv-close-btn"><X className="w-4 h-4 mr-1" /> {result ? "Close" : "Cancel"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
